import React from 'react';

import moment from 'moment';

import BarChart from '@src/assets/images/charts/Bar.svg';
import Counter from '@src/assets/images/charts/Counter.svg';
import Histogram from '@src/assets/images/charts/Histogram.svg';
import PieChart from '@src/assets/images/charts/Pie.svg';
import Table from '@src/assets/images/charts/Table.svg';
import Timeline from '@src/assets/images/charts/Timeline.svg';
import { translateString } from '@src/util/intl/translateString';

import IntlMessages from 'util/IntlMessages';

const TEMPLATES = 'templates';
export const defaultAggsConfig = {
  min: {
    color: '#FE9E15',
    displayType: 'bar',
  },
  max: {
    color: '#003366',
    displayType: 'bar',
  },
  count: {
    color: '#DDAFFF',
    displayType: 'area',
  },
  sum: {
    color: '#35AFFF',
    displayType: 'area',
  },
  avg: {
    color: '#59AA2B',
    displayType: 'line',
  },
};
export const LIST_TIMER_OPTIONS = [1, 3, 5];

export const CHART_TYPES = ['bar', 'line', 'area'];

export const METRICS_FILTER = 'metrics_filter';

export const makeSubtitle = ({ isRelative, dateType }) => {
  let intMessage = 'dashboard.chart.subtitle.';

  if (isRelative) {
    intMessage += 'relative';
    intMessage += `.${dateType}`;
  } else intMessage += 'absolute';

  return intMessage;
};

/* Managing the different forms of components in the dashboard to be used in the form handlers */
export const DASHBOARDS_FORMS = {
  STATISTIC_ONLY_NUMBER_COMPONENTS: 'statisticOnlyNumberComponents',
  SCHEDULE_COMPLIANCE_SUMMARY: 'scheduleComplianceSummary',
  EVENT_COUNTER_BY_TIME: 'eventCounterByTime',
  EVENT_COUNTER: 'eventCounter',
  EVENT_TABLE: 'eventTable',
  SCHEDULE_COMPLIANCE: 'scheduleCompliance',
  SCHEDULE_COMPLIANCE_HIST: 'scheduleComplianceHist',
  DEVIATIONS_FROM_TASK_COMPLIANCE: 'deviationsFromTaskCompliance',
  EVENT_COUNTER_NUMBER_COMPONENT: 'eventCounterNumberComponent',
};

/* Managing the different types of components in the dashboard to be used in the layout manager */
export const DASHBOARDS_EVENT_TYPE = {
  CHARTS: 'charts',
  TABLES: 'tables',
  PIE_CHARTS: 'pieCharts',
  COUNT_CHARTS: 'countCharts',
  COUNTER_CHARTS: 'counterCharts',
  BAR_CHARTS: 'barCharts',
};

/* List of options for forms, used to show in Metrics Drawer list of available components */
export const COMPONENTS_OF_METRICS_OPTIONS = [
  {
    value: DASHBOARDS_FORMS.EVENT_TABLE,
    label: translateString(`dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_TABLE}`),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_TABLE}.description`,
    ),
    icon: <img src={Table} alt="Table" />,
  },
  {
    value: DASHBOARDS_FORMS.EVENT_COUNTER,
    label: translateString(`dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_COUNTER}`),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_COUNTER}.description`,
    ),
    icon: <img src={PieChart} alt="Pie Chart" />,
  },
  {
    value: DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME}.description`,
    ),
    icon: <img src={Timeline} alt="Count Chart" />,
  },
  {
    value: DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS}.description`,
    ),
    icon: <img src={BarChart} alt="Bar Chart" />,
  },
  {
    value: DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY}.description`,
    ),
    icon: <img src={Counter} alt="Counter Chart" />,
  },
  {
    value: DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE}.description`,
    ),
    icon: <img src={BarChart} alt="Schedule Compliance" />,
  },
  {
    value: DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST}.description`,
    ),
    icon: <img src={Histogram} alt="Schedule Compliance Histogram" />,
  },
  {
    value: DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE}.description`,
    ),
    icon: <img src={Table} alt="Deviations from Task Compliance" />,
  },
  {
    value: DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT,
    label: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT}`,
    ),
    description: translateString(
      `dashboards.chart.form.component.${DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT}.description`,
    ),
    icon: <img src={Counter} alt="Counter Number Component" />,
  },
];

export const INPUT_TYPES_NOT_ALLOWED = ['video', 'file', 'photo', 'signature'];

// For a while we are going to use the same range when creating a new chart
export const RANGE_BY_DEFAULT = {
  isRelative: true,
  relativeAmount: 1,
  dateType: 'day',
  dateRange: [moment().subtract(6, 'month'), moment()],
};

export const DATE_FROM_DEFAULT = moment().subtract(7, 'days');

export const LAYOUT_BY_DEFAULT = { x: 1000, y: 1000, h: 6, w: 10 };

export const LAYOUT_BY_COMPONENT_TYPE = {
  [DASHBOARDS_EVENT_TYPE.CHARTS]: LAYOUT_BY_DEFAULT,
  [DASHBOARDS_EVENT_TYPE.TABLES]: { x: 0, y: 0, h: 9, w: 5 },
  [DASHBOARDS_EVENT_TYPE.PIE_CHARTS]: { x: 1000, y: 1000, h: 6, w: 5 },
  [DASHBOARDS_EVENT_TYPE.COUNT_CHARTS]: LAYOUT_BY_DEFAULT,
  [DASHBOARDS_EVENT_TYPE.COUNTER_CHARTS]: { x: 1000, y: 1000, h: 5, w: 5 },
  [DASHBOARDS_EVENT_TYPE.BAR_CHARTS]: { x: 1000, y: 1000, h: 5, w: 8 },
};

export const AUX_FIELDS_EXTRA = {
  userName: 'userName',
  eventDate: 'eventDate',
  location: 'location',
  eventType: 'eventType',
  division: 'division',
};
export const AUX_FIELDS_TABLE_FORM = [
  {
    componentId: AUX_FIELDS_EXTRA.userName,
    isActive: true,
    propertyName: <IntlMessages id="dashboards.events.table.auxFields.userName" />,
    order: null,
    index: 0,
  },
  {
    componentId: AUX_FIELDS_EXTRA.eventDate,
    isActive: true,
    propertyName: <IntlMessages id="dashboards.events.table.auxFields.eventDate" />,
    order: null,
    index: 1,
  },
  {
    componentId: AUX_FIELDS_EXTRA.eventType,
    isActive: true,
    propertyName: <IntlMessages id="dashboards.events.table.auxFields.eventType" />,
    order: null,
    index: 2,
  },
  {
    componentId: AUX_FIELDS_EXTRA.division,
    isActive: true,
    propertyName: <IntlMessages id="dashboards.events.table.auxFields.division" />,
    order: null,
    index: 3,
  },
  {
    componentId: AUX_FIELDS_EXTRA.location,
    isActive: true,
    propertyName: <IntlMessages id="dashboards.events.table.auxFields.location" />,
    order: null,
    index: 4,
  },
];

export const DASHBOARDS_COLLECTION = 'dashboards';

export const COLORS_PALETTE = [
  '#3f8dd8',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#A28DFF',
  '#FF6B6B',
  '#6D597A',
  '#4CAF50',
  '#FFD700',
  '#FF1493',
  '#20B2AA',
  '#B22222',
];

export const EXPRESSION_VALUE_TYPE = {
  PERCENTAGE: 'percentage',
  NUMBER: 'number',
};
export const EXPRESSION_VALUE_TYPE_DEFAULT = EXPRESSION_VALUE_TYPE.PERCENTAGE;

export const USERS_TYPE = {
  ALL: 'all',
  USERS: 'users',
  GROUPS: 'groups',
};

export const DIVISIONS_TYPE = {
  ALL: 'all',
  DIVISIONS: 'divisions',
};
export const GROUP_BY_OPTIONS_VALUES = {
  DIVISIONS: 'divisions',
  GROUPS: 'groups',
  USERS: 'users',
  SCHEDULES: 'schedules',
  TASKS: 'tasks',
  ROUTES: 'routes',
  TEMPLATES: 'templates',
};

export const GROUP_BY_OPTIONS = [
  {
    label: translateString('dashboards.chart.form.scheduleCompliance.groupBy.divisions'),
    value: GROUP_BY_OPTIONS_VALUES.DIVISIONS,
  },
  {
    label: translateString('dashboards.chart.form.scheduleCompliance.groupBy.groups'),
    value: GROUP_BY_OPTIONS_VALUES.GROUPS,
  },
  {
    label: translateString('dashboards.chart.form.scheduleCompliance.groupBy.users'),
    value: GROUP_BY_OPTIONS_VALUES.USERS,
  },
  {
    label: translateString('dashboards.chart.form.scheduleCompliance.groupBy.schedules'),
    value: GROUP_BY_OPTIONS_VALUES.SCHEDULES,
  },
  {
    label: translateString('dashboards.chart.form.scheduleCompliance.groupBy.tasks'),
    value: GROUP_BY_OPTIONS_VALUES.TASKS,
  },
  {
    label: translateString('dashboards.chart.form.scheduleCompliance.groupBy.routes'),
    value: GROUP_BY_OPTIONS_VALUES.ROUTES,
  },
];

/* Pass task by template */
/**
 * @param {string} record
 * @returns {string}
 * @description We need to pass task by template to the backend because the backend is not able to handle tasks and templates together.
 */
export const byPassTaskByTemplate = record => {
  switch (record) {
    case GROUP_BY_OPTIONS_VALUES.TASKS:
      return TEMPLATES;
    case TEMPLATES:
      return GROUP_BY_OPTIONS_VALUES.TASKS;
    default:
      return record;
  }
};

export const GROUP_BY_DEFAULT = 'divisions';

export const INITIAL_DISABLE_SCHEDULE_TYPE = {
  null: false,
  schedules: false,
  tasks: false,
  checkpoint_route: false,
};

export const ALL_TEMPLATES = 'all';
export const TEMPLATE_IDS_DEFAULT = [ALL_TEMPLATES];
